import { useEffect, useRef } from 'react';
import type {
  NextPage,
  GetServerSideProps,
  GetServerSidePropsContext,
} from 'next';
import { datadogRum } from '@datadog/browser-rum';
import { z } from 'zod';
import { G } from '@mobily/ts-belt';
import { Base64 } from 'js-base64';
import { serverLogger } from '../../utils/loggers/server';
import {
  ACCEPT_INVITE_COOKIE_NAME,
  isNonEmptyString,
} from '../../utils/helpers';
import { getCachedEntryByUrl } from '../../utils/requests/contentstack/cachedRequests';
import { ModularPageWrapper } from '../../components/ModularPageWrapper';
import { modularPageWrapperSchema } from '../../components/ModularPageWrapper/schema';
import type { ModularComponent } from '../../components/ModularPageWrapper/ModularPageWrapper';

type AcceptInviteSlugPageProps = z.infer<typeof modularPageWrapperSchema> & {
  isVisionPage: boolean;
};

const getPersonalizedHeroModules = ({
  modules,
  req,
}: {
  modules: ModularComponent[];
  req: GetServerSidePropsContext['req'];
}): ModularComponent[] => {
  // When a user clicks a referral link, Referrals-UI sets a cookie with encoded personalized credit copy
  // Ex: "Kat has given you $25 to try Stitch Fix" and redirects the user to the relevant /accept-invite route
  // If the user has that cookie we personalize the headline in the hero, if not we use a default
  const acceptInviteCookie = req.cookies[ACCEPT_INVITE_COOKIE_NAME];

  if (!isNonEmptyString(acceptInviteCookie)) {
    return modules;
  }

  const first = modules[0];

  if (first.type !== 'hero') {
    return modules;
  }

  const heroProps = first.props;
  const firstItem = heroProps.items?.[0];

  if (!firstItem) {
    return modules;
  }

  const personalizedHero: ModularComponent = {
    type: 'hero',
    props: {
      ...heroProps,
      items: [
        {
          ...firstItem,
          title: {
            ...firstItem.title,
            children: [
              {
                ...firstItem.title.children[0],
                // Using Base64.decode() instead of atob() because the encoded string includes non breaking spaces which is a known issue for atob()
                children: [{ text: Base64.decode(acceptInviteCookie) }],
              },
            ],
          },
        },
      ],
    },
  };

  return [personalizedHero, ...modules.slice(1)];
};

export const getServerSideProps: GetServerSideProps<
  AcceptInviteSlugPageProps
> = async ({ query, locale, req }) => {
  const slugs = Array.isArray(query.slug) ? query.slug : [query.slug ?? ''];
  const isVisionPage = slugs[0] === 'vision';
  const pageUrl = isNonEmptyString(slugs[0])
    ? `/accept-invite/${slugs.join('/')}`
    : `/accept-invite`;

  const language = locale;

  const entryRequest = getCachedEntryByUrl({
    contentType: 'modular_page',
    url: pageUrl,
    language,
    schema: modularPageWrapperSchema,
    logger: serverLogger,
  });

  let entryResponse;

  try {
    entryResponse = await entryRequest;
  } catch (error: unknown) {
    serverLogger({
      /**
       * not well-documented, but 141 is the code contentstack returns when an
       * undefined entry is requested. At time of comment, it is accompanied
       * by "The requested entry doesn't exist" text.
       */
      level: G.isObject(error) && error?.error_code === 141 ? 'warn' : 'error',
      team: 'client-onboarding',
      message: `Failed to fetch page data - /accept-invite/`,
      req,
      context: {
        error,
        page: '/accept-invite/[...slug]',
        url: pageUrl,
      },
    });
  }

  if (G.isUndefined(entryResponse)) {
    serverLogger({
      level: 'warn',
      team: 'client-onboarding',
      message: 'Required page data is undefined - /accept-invite/',
      req,
      context: {
        page: '/accept-invite/[...slug]',
        url: pageUrl,
      },
    });

    return {
      notFound: true,
    };
  }

  return {
    props: {
      isVisionPage,
      seoData: entryResponse.data.seoData,
      modules: getPersonalizedHeroModules({
        modules: entryResponse.data.modules,
        req,
      }),
    },
  };
};

const AcceptInviteSlugPage: NextPage<AcceptInviteSlugPageProps> = ({
  seoData,
  modules,
  isVisionPage,
}) => {
  const hasTrackedVisionViewRef = useRef(false);

  useEffect(() => {
    if (!isVisionPage || hasTrackedVisionViewRef.current) {
      return;
    }

    hasTrackedVisionViewRef.current = true;
    datadogRum.addAction('accept_invite.vision.viewed');
  }, [isVisionPage]);

  return <ModularPageWrapper seoData={seoData} modules={modules} />;
};

export default AcceptInviteSlugPage;
